import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"
import Button from "@mui/material/Button"
import React, { useEffect, useState } from "react"
import { NavLink, useNavigate } from "react-router-dom"
import { PATHS } from "../../../constants/paths"
import { formatCountdown } from "../utils/formatCountdown"
import LoadingButton from "@mui/lab/LoadingButton"
import { connectHandler } from "../../../utils/connectHandler"
import { useAppDispatch, useAppSelector } from "../../../store"
import { useDialog } from "../../../context/DialogContext"
import JoinConfirmDialog from "./dialogs/JoinConfirmDialog"
import GameDialog from "./dialogs/GameDialog"

import { TournamentResultDialog } from "./dialogs/TournamentResultDialog"

export const TournamentCard = ({
  id,
  type,
  title,
  description,
  prize_pool,
  entry_fee,
  players,
  time_left,
  starts_in,
  image,
  participation,
}) => {
  const initialSeconds = Number(starts_in ?? time_left ?? 0)
  const [remainingSeconds, setRemainingSeconds] = useState(initialSeconds)

  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.user)

  const [createDialog, closeDialog] = useDialog()
  const [openGameDialog, closeGameDialog] = useDialog()
  const [openTournamentResultDialog, closeTournamentResultDialog] = useDialog()

  const openResultDialog = (result = {}) => {
    openTournamentResultDialog({
      children: (
        <TournamentResultDialog
          closeDialog={closeTournamentResultDialog}
          zombiesKilled={result.zombiesKilled}
          waveReached={result.waveReached}
          bossesKilled={result.bossesKilled}
          score={result.score}
          timePlayed={result.timePlayed}
          position={result.position}
          totalPlayers={result.totalPlayers}
          bestScore={result.bestScore}
          variant={result.variant || "complete"}
        />
      ),
    })
  }

  useEffect(() => {
    setRemainingSeconds(Number(starts_in ?? time_left ?? 0))
  }, [starts_in, time_left])

  useEffect(() => {
    const timer = setInterval(() => {
      setRemainingSeconds((prevSeconds) => {
        if (prevSeconds <= 0) {
          return 0
        }

        return prevSeconds - 1
      })
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [])

  return (
    <Box
      onClick={() => navigate(`${PATHS.TOURNAMENT_DETAILS.PATH}${id}`)}
      sx={{
        p: 1,
        border: "1px solid rgba(198, 146, 76, 0.25)",
        borderRadius: 1,
        background: `url(${image}) top / contain no-repeat, #000`,
        minHeight: 600,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        cursor: "pointer",
      }}
    >
      {/* Card body */}
      <Box
        sx={{
          minHeight: 200,
          p: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Typography
          sx={{
            color: "#c7954f",
            fontSize: 14,
            fontWeight: 900,
            letterSpacing: "0.08em",
          }}
        >
          {type.toUpperCase()}
        </Typography>

        <Typography
          sx={{
            mt: 0.5,
          }}
          variant="h4"
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt: 0.7,
            color: "#dedede",
            fontSize: 16,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            py: 1,
            mt: 0.5,
            borderTop: "1px solid rgba(198,146,76,0.14)",
            borderBottom: "1px solid rgba(198,146,76,0.14)",
          }}
        >
          {[
            ["PRIZE POOL", prize_pool],
            ["ENTRY FEE", entry_fee],
            ["PLAYERS", players],
          ].map(([label, value]) => (
            <Box key={label}>
              <Typography
                sx={{
                  color: value === players ? "#999" : "#c49654",
                  fontSize: 14,
                  fontWeight: 900,
                  letterSpacing: "0.06em",
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  mt: 0.3,
                  color: "#e4ded1",
                  fontSize: 18,
                  fontWeight: 800,
                }}
              >
                {value} {value === players ? "" : "USDT"}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "end",
            gap: 0.8,
            mt: 1.7,
            color: "#d79b4d",
          }}
        >
          <AccessTimeOutlinedIcon sx={{ fontSize: 17, color: "#ccc" }} />
          <Box>
            <Typography
              sx={{
                color: "#c49654",
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1,
                letterSpacing: "0.06em",
              }}
            >
              {starts_in ? "STARTS IN" : "TIME LEFT"}
            </Typography>
            <Typography
              sx={{
                mt: 0.3,
                color: "#eee",
                fontSize: 16,
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {formatCountdown(remainingSeconds)}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* end of Card body */}

      {/* Card footer */}
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{ cursor: "default" }}
      >
        <Button
          variant="contained"
          size="large"
          fullWidth
          sx={{ mt: 2 }}
          component={NavLink}
          to={`${PATHS.TOURNAMENT_DETAILS.PATH}${id}`}
        >
          View details
        </Button>

        {!user.address ? (
          <LoadingButton
            variant="contained"
            size="large"
            sx={{
              background: "linear-gradient(135deg, #FF6B00, #C13D00)",
              mt: 2,
              zIndex: 0,

              "&::before": {
                content: '""',
                borderRadius: "inherit",
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(135deg, rgb(230,85,0), rgb(160,45,0))",
                opacity: 0,
                transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                zIndex: -1,
              },

              "&:hover::before": {
                opacity: 1,
              },
            }}
            onClick={(event) => {
              event.stopPropagation()
              connectHandler(dispatch)
            }}
            fullWidth
          >
            Connect wallet
          </LoadingButton>
        ) : (
          <>
            {participation ? (
              <LoadingButton
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  background: "linear-gradient(135deg, #39FF14, #1E9600)",
                  mt: 2,
                  zIndex: 0,
                  "&::before": {
                    content: '""',
                    borderRadius: "inherit",
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(135deg, #2bb80e, #146b00)",
                    opacity: 0,
                    transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                    zIndex: -1,
                  },

                  "&:hover::before": {
                    opacity: 1,
                  },
                }}
                onClick={(event) => {
                  event.stopPropagation()
                  createDialog({
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    children: (
                      <GameDialog
                        tournamentId={id}
                        user={user}
                        closeDialog={closeGameDialog}
                        onGameFinished={openResultDialog}
                      />
                    ),
                  })
                }}
              >
                Play Now
              </LoadingButton>
            ) : (
              <LoadingButton
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  background: "linear-gradient(135deg, #FF6B00, #C13D00)",
                  mt: 2,
                  zIndex: 0,
                  "&::before": {
                    content: '""',
                    borderRadius: "inherit",
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(135deg, rgb(230,85,0), rgb(160,45,0))",
                    opacity: 0,
                    transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                    zIndex: -1,
                  },
                  "&:hover::before": {
                    opacity: 1,
                  },
                }}
                onClick={(event) => {
                  event.stopPropagation()
                  createDialog({
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    children: (
                      <JoinConfirmDialog
                        closeDialog={closeDialog}
                        tournamentId={id}
                        entryFee={entry_fee}
                        user={user}
                      />
                    ),
                  })
                }}
              >
                Join Tournament
              </LoadingButton>
            )}
          </>
        )}
      </Box>
      {/* end of Card Footer */}
    </Box>
  )
}
